<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Head, Link, router } from '@inertiajs/vue3'
import { trackEvent } from '@/composables/useAnalytics'
import { useFilterTransition } from '@/composables/useFilterTransition'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import { Button } from '@/Components/ui/button'
import TutorsListingStatsCard from '@/Components/TutorsListingStatsCard.vue'
import TutorsListingFilterSort from '@/Components/TutorsListingFilterSort.vue'
import TutorCard from '@/Components/TutorCard.vue'
import {
  ChevronLeft,
  ChevronRight,
  Star,
  Eye,
  Clock,
  Play,
  Flame,
  Search,
  X,
  Filter,
  ChevronDown,
  LockOpen,
  Heart,
  Bookmark,
  Users,
  BookOpen,
} from 'lucide-vue-next'

interface Tutor {
  id: number
  slug: string
  name: string
  avatar: string
  qualification: string
  rating: number
  reviews_count: number
  students_count: number
  subjects: Array<{ id: number; name: string; color: string }>
  levels: Array<{ id: number; name: string; color: string }>
  examboards: string[]
  bio: string
  services_offered: string[]
  price_base: number
  price_silver: number
  price_gold: number
  teaching_experience?: string
  teaching_style?: string
}

interface Subject {
  id: number
  name: string
  slug: string
  color?: string
  icon?: string
}

interface Level {
  id: number
  name: string
  slug: string
}

interface Examboard {
  id: number
  name: string
  slug: string
}

interface Stats {
  videos: number
  groups: number
  one_on_one: number
  materials: number
  tutors_with_videos: number
  tutors_with_materials: number
}

interface Subscription {
  teacher_id: number
  tier: string
  expires_in: number | null
}

interface Props {
  activeTab: 'tutors' | 'library'
  // Tutors tab props
  tutors?: {
    data: Tutor[]
    current_page: number
    last_page: number
    per_page: number
    total: number
    from: number
    to: number
  }
  stats?: Stats
  // Library tab props
  contents?: {
    data: any[]
    current_page: number
    last_page: number
    total: number
    prev_page_url: string | null
    next_page_url: string | null
  }
  featuredContent?: any
  // Shared props
  subjects: Subject[]
  levels: Level[]
  examboards?: Examboard[]
  filters: Record<string, any>
  subscriptions?: Subscription[]
}

const props = withDefaults(defineProps<Props>(), {
  subscriptions: () => [],
})

// --- Tab switching ---
const switchTab = (tab: 'tutors' | 'library') => {
  if (tab === props.activeTab) return
  router.get('/student/explore', { tab }, {
    preserveState: false,
  })
}

// --- Tutors tab state ---
const sortBy = ref(props.filters.sort || 'popular')
const viewMode = ref<'grid' | 'list'>('grid')

const toArray = (val: any): string[] =>
  Array.isArray(val) ? val : (val ? [val] : [])

const activeServiceFilters = ref<string[]>(toArray(props.filters.teaching_methods))
const activeSubjectSlugs = ref<string[]>(
  toArray(props.filters.subjects).concat(
    props.filters.subject && !toArray(props.filters.subjects).includes(props.filters.subject)
      ? [props.filters.subject]
      : []
  )
)
const activeLevelSlugs = ref<string[]>(toArray(props.filters.levels))
const activeExamboardSlugs = ref<string[]>(toArray(props.filters.examboards))

const statsData = computed(() => ({
  videos: props.stats?.videos ?? 0,
  groups: props.stats?.groups ?? 0,
  one_on_one: props.stats?.one_on_one ?? 0,
  materials: props.stats?.materials ?? 0,
  tutors_with_videos: props.stats?.tutors_with_videos ?? 0,
  tutors_with_materials: props.stats?.tutors_with_materials ?? 0,
}))

const serviceToSlugMap: Record<string, string> = {
  'videos': 'recorded-video-content',
  'materials': 'study-material',
  'group_sessions': 'group-tutoring',
  'one_on_one': '11-tutoring',
}

const buildTutorFilterParams = (): Record<string, any> => {
  const params: any = { tab: 'tutors' }

  if (activeSubjectSlugs.value.length > 0) params.subjects = activeSubjectSlugs.value
  if (activeLevelSlugs.value.length > 0) params.levels = activeLevelSlugs.value
  if (activeExamboardSlugs.value.length > 0) params.examboards = activeExamboardSlugs.value
  if (activeServiceFilters.value.length > 0) params.teaching_methods = activeServiceFilters.value
  if (props.filters.price_range) params.price_range = props.filters.price_range
  if (props.filters.rating) params.rating = props.filters.rating
  if (props.filters.min_students) params.min_students = props.filters.min_students
  if (sortBy.value) params.sort = sortBy.value

  return params
}

/**
 * Same crossfade as the public listing: the grid dims while the server answers
 * rather than being torn down and re-staggered, so filtering reads as the
 * results being replaced instead of the page flashing.
 */
const { pending, visit } = useFilterTransition('/student/explore')

const navigateTutorsWithFilters = (preserveScroll = true) => {
  visit(buildTutorFilterParams(), { preserveScroll })
}

const handleServiceFilter = (service: string) => {
  const slug = serviceToSlugMap[service]
  const index = activeServiceFilters.value.indexOf(slug)
  if (index > -1) {
    activeServiceFilters.value.splice(index, 1)
  } else {
    activeServiceFilters.value.push(slug)
  }
  navigateTutorsWithFilters()
}

const isServiceFilterActive = (service: string): boolean => {
  const slug = serviceToSlugMap[service]
  return activeServiceFilters.value.includes(slug)
}

const handleFilterChange = (filters: { subjects: string[]; levels: string[]; examboards: string[] }) => {
  activeSubjectSlugs.value = filters.subjects
  activeLevelSlugs.value = filters.levels
  activeExamboardSlugs.value = filters.examboards
  trackEvent('applyFilter', {
    subjects: filters.subjects,
    levels: filters.levels,
    examboards: filters.examboards,
  })
  navigateTutorsWithFilters()
}

const handleSortChange = (sort: string) => {
  sortBy.value = sort
  navigateTutorsWithFilters()
}

const handleViewChange = (view: 'grid' | 'list') => {
  viewMode.value = view
}

const changeTutorsPage = (page: number) => {
  router.get('/student/explore', { ...buildTutorFilterParams(), page }, {
    preserveState: true,
    preserveScroll: false,
  })
}

// --- Library tab state ---
/**
 * Enter takes you to the results.
 *
 * The grid begins below the search box, so on a short window typing and
 * pressing Enter moved nothing the reader could see.
 */
const resultsRef = ref(null)
const showResults = () => resultsRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })

const searchQuery = ref(props.filters?.search || '')
const selectedSubjects = ref(props.filters?.subjects || [])
const selectedLevels = ref(props.filters?.levels || [])
const selectedExamboards = ref(props.filters?.examboards || [])

const isSubjectOpen = ref(true)
const isLevelOpen = ref(true)
const isExamboardOpen = ref(true)

const formatDuration = (seconds: number) => {
  if (!seconds) return '00:00'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

const formatViews = (count: number) => {
  if (!count) return '0'
  if (count >= 1000) {
    return (count / 1000).toFixed(1) + 'K'
  }
  return count.toString()
}

const formatTimeAgo = (date: string) => {
  const now = new Date()
  const then = new Date(date)
  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`
  if (seconds < 2592000) return `${Math.floor(seconds / 604800)} weeks ago`
  return `${Math.floor(seconds / 2592000)} months ago`
}

const getSubjectColor = (subject: any) => {
  if (!subject) return '#6B7280'
  return subject.color || '#6B7280'
}

const toggleSubject = (subjectSlug: string) => {
  const index = selectedSubjects.value.indexOf(subjectSlug)
  if (index > -1) {
    selectedSubjects.value.splice(index, 1)
  } else {
    selectedSubjects.value.push(subjectSlug)
  }
  applyLibraryFilters()
}

const toggleLevel = (levelSlug: string) => {
  const index = selectedLevels.value.indexOf(levelSlug)
  if (index > -1) {
    selectedLevels.value.splice(index, 1)
  } else {
    selectedLevels.value.push(levelSlug)
  }
  applyLibraryFilters()
}

const toggleExamboard = (examboardSlug: string) => {
  const index = selectedExamboards.value.indexOf(examboardSlug)
  if (index > -1) {
    selectedExamboards.value.splice(index, 1)
  } else {
    selectedExamboards.value.push(examboardSlug)
  }
  applyLibraryFilters()
}

const removeSubject = (subjectSlug: string) => {
  const index = selectedSubjects.value.indexOf(subjectSlug)
  if (index > -1) {
    selectedSubjects.value.splice(index, 1)
    applyLibraryFilters()
  }
}

const removeLevel = (levelSlug: string) => {
  const index = selectedLevels.value.indexOf(levelSlug)
  if (index > -1) {
    selectedLevels.value.splice(index, 1)
    applyLibraryFilters()
  }
}

const removeExamboard = (examboardSlug: string) => {
  const index = selectedExamboards.value.indexOf(examboardSlug)
  if (index > -1) {
    selectedExamboards.value.splice(index, 1)
    applyLibraryFilters()
  }
}

const applyLibraryFilters = () => {
  const params: Record<string, any> = { tab: 'library' }
  if (searchQuery.value) params.search = searchQuery.value
  if (selectedSubjects.value.length > 0) params.subjects = selectedSubjects.value
  if (selectedLevels.value.length > 0) params.levels = selectedLevels.value
  if (selectedExamboards.value.length > 0) params.examboards = selectedExamboards.value

  router.get('/student/explore', params, {
    preserveState: true,
    preserveScroll: true,
  })
}

const resetLibraryFilters = () => {
  searchQuery.value = ''
  selectedSubjects.value = []
  selectedLevels.value = []
  selectedExamboards.value = []
  applyLibraryFilters()
}

let searchTimeout: ReturnType<typeof setTimeout>
watch(searchQuery, (newQuery) => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    if (newQuery) {
      trackEvent('search', { search_term: newQuery })
    }
    applyLibraryFilters()
  }, 500)
})
</script>

<template>
  <Head :title="activeTab === 'tutors' ? 'Explore Tutors' : 'Content Library'" />

  <DashboardLayout>
    <div class="space-y-6">
      <!-- Tab Bar -->
      <div class="border-b border-gray-200">
        <nav class="flex gap-6">
          <button
            @click="switchTab('tutors')"
            :class="[
              'pb-3 px-1 text-sm font-semibold border-b-2 transition-colors',
              activeTab === 'tutors'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            <span class="flex items-center gap-2">
              <Users class="w-4 h-4" />
              Tutors
            </span>
          </button>
          <button
            @click="switchTab('library')"
            :class="[
              'pb-3 px-1 text-sm font-semibold border-b-2 transition-colors',
              activeTab === 'library'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            <span class="flex items-center gap-2">
              <BookOpen class="w-4 h-4" />
              Content Library
            </span>
          </button>
        </nav>
      </div>

      <!-- ==================== TUTORS TAB ==================== -->
      <template v-if="activeTab === 'tutors' && tutors">
        <!--
          The four counting tiles that opened this tab have gone, as on the
          public listing: they counted things rather than helping anyone choose,
          and one of them counted group sessions, which are no longer sold. The
          same counts remain available as the filter toggles below.
        -->

        <!-- Filter Buttons -->
        <section class="flex flex-wrap gap-3">
          <TutorsListingStatsCard
            :count="statsData.videos"
            subtitle="Videos"
            variant="video"
            view="simple"
            :is-active="isServiceFilterActive('videos')"
            @click="handleServiceFilter('videos')"
          />
          <!-- Group sessions are not on sale while Bindle Live is built, so
               filtering by them would only ever return tutors who cannot
               deliver them. -->
          <TutorsListingStatsCard
            :count="statsData.one_on_one"
            subtitle="1:1 Tutoring"
            variant="tutor"
            view="simple"
            :is-active="isServiceFilterActive('one_on_one')"
            @click="handleServiceFilter('one_on_one')"
          />
          <TutorsListingStatsCard
            :count="statsData.materials"
            subtitle="Materials"
            variant="material"
            view="simple"
            :is-active="isServiceFilterActive('materials')"
            @click="handleServiceFilter('materials')"
          />
        </section>

        <!-- Filter & Sort Section -->
        <TutorsListingFilterSort
          :total-count="tutors.total"
          :initial-view="viewMode"
          :initial-sort="sortBy"
          :subjects="subjects"
          :levels="levels"
          :examboards="examboards"
          :initial-subjects="activeSubjectSlugs"
          :initial-levels="activeLevelSlugs"
          :initial-examboards="activeExamboardSlugs"
          @filter-change="handleFilterChange"
          @sort-change="handleSortChange"
          @view-change="handleViewChange"
        />

        <!-- Tutors Grid -->
        <section
          ref="resultsRef"
          :class="[
            'scroll-mt-24',
            'grid gap-6 transition-[opacity,transform] duration-200 ease-brand',
            pending ? 'translate-y-1 opacity-40' : 'translate-y-0 opacity-100',
            viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 lg:grid-cols-2',
          ]"
          aria-live="polite"
          :aria-busy="pending"
        >
          <TutorCard
            v-for="tutor in tutors.data"
            :key="tutor.id"
            :tutor="tutor"
            :view-mode="viewMode"
            link-prefix="/student/tutors"
          />
        </section>

        <!-- Empty State -->
        <div v-if="tutors.data.length === 0" class="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search class="w-8 h-8 text-gray-400" />
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">No Tutors Found</h3>
          <p class="text-sm text-gray-500 max-w-md mx-auto">
            Try adjusting your filters to find more tutors.
          </p>
        </div>

        <!-- Pagination -->
        <section v-if="tutors.last_page > 1" class="flex items-center justify-between">
          <p class="text-gray-600 text-sm">
            Showing {{ tutors.from }}-{{ tutors.to }} of {{ tutors.total }} tutors
          </p>

          <div class="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              :disabled="tutors.current_page === 1"
              @click="changeTutorsPage(tutors.current_page - 1)"
            >
              <ChevronLeft class="w-4 h-4" />
            </Button>

            <template v-for="page in tutors.last_page" :key="page">
              <Button
                v-if="page === 1 || page === tutors.last_page || (page >= tutors.current_page - 1 && page <= tutors.current_page + 1)"
                :variant="page === tutors.current_page ? 'default' : 'outline'"
                size="sm"
                class="min-w-[40px]"
                @click="changeTutorsPage(page)"
              >
                {{ page }}
              </Button>
              <span
                v-else-if="page === tutors.current_page - 2 || page === tutors.current_page + 2"
                class="text-gray-500 px-2"
              >
                ...
              </span>
            </template>

            <Button
              variant="outline"
              size="sm"
              :disabled="tutors.current_page === tutors.last_page"
              @click="changeTutorsPage(tutors.current_page + 1)"
            >
              <ChevronRight class="w-4 h-4" />
            </Button>
          </div>
        </section>
      </template>

      <!-- ==================== LIBRARY TAB ==================== -->
      <template v-if="activeTab === 'library' && contents">
        <!-- Search Bar -->
        <section class="bg-white rounded-xl border border-gray-200 p-4">
          <div class="relative">
            <div class="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search class="w-5 h-5" />
            </div>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by title, topic, or tutor name..."
              @keyup.enter="showResults"
              class="w-full pl-12 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-gray-900"
            />
            <button
              v-if="searchQuery"
              @click="searchQuery = ''"
              class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X class="w-5 h-5" />
            </button>
          </div>
        </section>

        <!-- Main Content with Sidebar -->
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <!-- Filters Sidebar -->
          <aside class="lg:col-span-1">
            <div class="bg-white rounded-xl border border-gray-200 p-5">
              <div class="flex items-center justify-between mb-5">
                <h2 class="text-base font-bold text-gray-900 flex items-center">
                  <Filter class="w-4 h-4 mr-2" />
                  Filters
                </h2>
                <button
                  v-if="selectedSubjects.length > 0 || selectedLevels.length > 0 || selectedExamboards.length > 0"
                  @click="resetLibraryFilters"
                  class="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Reset
                </button>
              </div>

              <!-- Subject Filter Accordion -->
              <div class="mb-4">
                <button
                  @click="isSubjectOpen = !isSubjectOpen"
                  class="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <h3 class="font-bold text-gray-900 text-sm">Subject</h3>
                  <ChevronDown
                    class="w-4 h-4 text-gray-600 transition-transform duration-200"
                    :class="{ 'rotate-180': isSubjectOpen }"
                  />
                </button>
                <div v-if="selectedSubjects.length > 0" class="px-2.5 pt-2 pb-1 flex flex-wrap gap-1.5">
                  <span
                    v-for="subjectSlug in selectedSubjects"
                    :key="`selected-${subjectSlug}`"
                    class="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md text-xs font-medium"
                  >
                    {{ subjects.find(s => s.slug === subjectSlug)?.name }}
                    <button @click="removeSubject(subjectSlug)" class="ml-1 hover:text-gray-900">
                      <X class="w-3 h-3" />
                    </button>
                  </span>
                </div>
                <Transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="transform opacity-0 -translate-y-2"
                  enter-to-class="transform opacity-100 translate-y-0"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="transform opacity-100 translate-y-0"
                  leave-to-class="transform opacity-0 -translate-y-2"
                >
                  <div v-show="isSubjectOpen" class="pt-2 space-y-1 max-h-64 overflow-y-auto">
                    <label
                      v-for="subject in subjects"
                      :key="subject.slug"
                      class="flex items-center p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        :value="subject.slug"
                        :checked="selectedSubjects.includes(subject.slug)"
                        @change="toggleSubject(subject.slug)"
                        class="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span class="ml-3 text-sm text-gray-700 flex-1">{{ subject.name }}</span>
                    </label>
                  </div>
                </Transition>
              </div>

              <!-- Level Filter Accordion -->
              <div class="mb-4 pb-4 border-t border-gray-200 pt-4">
                <button
                  @click="isLevelOpen = !isLevelOpen"
                  class="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <h3 class="font-bold text-gray-900 text-sm">Level</h3>
                  <ChevronDown
                    class="w-4 h-4 text-gray-600 transition-transform duration-200"
                    :class="{ 'rotate-180': isLevelOpen }"
                  />
                </button>
                <div v-if="selectedLevels.length > 0" class="px-2.5 pt-2 pb-1 flex flex-wrap gap-1.5">
                  <span
                    v-for="levelSlug in selectedLevels"
                    :key="`selected-${levelSlug}`"
                    class="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md text-xs font-medium"
                  >
                    {{ levels.find(l => l.slug === levelSlug)?.name }}
                    <button @click="removeLevel(levelSlug)" class="ml-1 hover:text-gray-900">
                      <X class="w-3 h-3" />
                    </button>
                  </span>
                </div>
                <Transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="transform opacity-0 -translate-y-2"
                  enter-to-class="transform opacity-100 translate-y-0"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="transform opacity-100 translate-y-0"
                  leave-to-class="transform opacity-0 -translate-y-2"
                >
                  <div v-show="isLevelOpen" class="pt-2 space-y-1">
                    <label
                      v-for="level in levels"
                      :key="level.slug"
                      class="flex items-center p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        :value="level.slug"
                        :checked="selectedLevels.includes(level.slug)"
                        @change="toggleLevel(level.slug)"
                        class="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span class="ml-3 text-sm text-gray-700">{{ level.name }}</span>
                    </label>
                  </div>
                </Transition>
              </div>

              <!-- Exam Board Filter Accordion -->
              <div class="pb-4 border-t border-gray-200 pt-4">
                <button
                  @click="isExamboardOpen = !isExamboardOpen"
                  class="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <h3 class="font-bold text-gray-900 text-sm">Exam Board</h3>
                  <ChevronDown
                    class="w-4 h-4 text-gray-600 transition-transform duration-200"
                    :class="{ 'rotate-180': isExamboardOpen }"
                  />
                </button>
                <div v-if="selectedExamboards.length > 0" class="px-2.5 pt-2 pb-1 flex flex-wrap gap-1.5">
                  <span
                    v-for="examboardSlug in selectedExamboards"
                    :key="`selected-${examboardSlug}`"
                    class="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md text-xs font-medium"
                  >
                    {{ examboards?.find(e => e.slug === examboardSlug)?.name }}
                    <button @click="removeExamboard(examboardSlug)" class="ml-1 hover:text-gray-900">
                      <X class="w-3 h-3" />
                    </button>
                  </span>
                </div>
                <Transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="transform opacity-0 -translate-y-2"
                  enter-to-class="transform opacity-100 translate-y-0"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="transform opacity-100 translate-y-0"
                  leave-to-class="transform opacity-0 -translate-y-2"
                >
                  <div v-show="isExamboardOpen" class="pt-2 space-y-1">
                    <label
                      v-for="examboard in examboards"
                      :key="examboard.slug"
                      class="flex items-center p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        :value="examboard.slug"
                        :checked="selectedExamboards.includes(examboard.slug)"
                        @change="toggleExamboard(examboard.slug)"
                        class="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span class="ml-3 text-sm text-gray-700">{{ examboard.name }}</span>
                    </label>
                  </div>
                </Transition>
              </div>
            </div>
          </aside>

          <!-- Main Content Area -->
          <div class="lg:col-span-3">
            <!-- Featured Content -->
            <section v-if="featuredContent && !searchQuery" class="mb-6">
              <div class="flex items-center justify-between mb-4">
                <h2 class="text-lg font-bold text-gray-900 flex items-center">
                  <Star class="text-yellow-500 mr-2 w-5 h-5 fill-yellow-500" />
                  Featured Today
                </h2>
              </div>

              <Link :href="`/student/library/${featuredContent.slug}`" class="block bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-md transition-shadow">
                <div class="grid md:grid-cols-3 gap-4">
                  <div class="relative md:col-span-1">
                    <div class="h-full min-h-[200px] bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                      <Play class="text-white w-14 h-14 opacity-80" />
                    </div>
                    <div class="absolute top-3 right-3 bg-black/70 text-white px-2.5 py-1 rounded-lg text-xs font-medium">
                      {{ formatDuration(featuredContent.duration) }}
                    </div>
                    <div class="absolute bottom-3 left-3 bg-red-500 text-white px-2.5 py-1 rounded-lg text-xs font-bold flex items-center">
                      <Flame class="mr-1 w-3.5 h-3.5" />
                      TRENDING
                    </div>
                  </div>

                  <div class="md:col-span-2 p-5">
                    <div class="flex items-start justify-between mb-3">
                      <div class="flex items-center gap-3">
                        <img
                          :src="featuredContent.teacher?.avatar || null"
                          class="w-10 h-10 rounded-full"
                          :alt="featuredContent.teacher?.name"
                        />
                        <div>
                          <h3 class="font-bold text-gray-900">{{ featuredContent.title }}</h3>
                          <p class="text-sm text-gray-500">by {{ featuredContent.teacher?.name }}</p>
                        </div>
                      </div>
                    </div>

                    <p class="text-gray-600 text-sm mb-4 line-clamp-2">
                      {{ featuredContent.description }}
                    </p>

                    <div class="flex items-center justify-between flex-wrap gap-3">
                      <div class="flex items-center gap-4 text-sm">
                        <span class="flex items-center text-gray-600">
                          <Eye class="mr-1.5 w-4 h-4" />
                          {{ formatViews(featuredContent.view_count) }} views
                        </span>
                        <span class="flex items-center text-gray-600">
                          <Star class="text-yellow-400 mr-1.5 w-4 h-4 fill-yellow-400" />
                          {{ featuredContent.ratings_avg_score ? Number(featuredContent.ratings_avg_score).toFixed(1) : 'N/A' }}
                        </span>
                        <span class="flex items-center text-gray-600">
                          <Clock class="mr-1.5 w-4 h-4" />
                          {{ formatTimeAgo(featuredContent.created_at) }}
                        </span>
                      </div>

                      <div class="flex items-center gap-2">
                        <span
                          v-if="featuredContent.is_free"
                          class="flex items-center px-2 py-1 bg-emerald-100 text-emerald-700 rounded-lg font-medium text-xs"
                        >
                          <LockOpen class="w-3.5 h-3.5 mr-1" />
                          FREE
                        </span>
                        <span
                          v-if="featuredContent.subject"
                          class="px-2.5 py-1 rounded-full font-medium text-xs"
                          :style="{
                            backgroundColor: `${getSubjectColor(featuredContent.subject)}20`,
                            color: getSubjectColor(featuredContent.subject)
                          }"
                        >
                          {{ featuredContent.subject.name }}
                        </span>
                        <span class="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm flex items-center">
                          <Play class="mr-1.5 w-4 h-4" />
                          Watch Now
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </section>

            <!-- All Content Grid -->
            <section>
              <div class="flex items-center justify-between mb-4">
                <h2 class="text-lg font-bold text-gray-900">All Content</h2>
                <span v-if="contents.data.length > 0" class="text-sm text-gray-500">
                  Showing {{ contents.data.length }} of {{ contents.total }} items
                </span>
              </div>

              <div v-if="contents.data.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
                <Link
                  v-for="content in contents.data"
                  :key="content.id"
                  :href="`/student/library/${content.slug}`"
                  class="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-md transition-all hover:-translate-y-0.5"
                >
                  <div class="relative">
                    <div
                      class="h-40 bg-gradient-to-br flex items-center justify-center"
                      :class="[
                        content.id % 5 === 0 ? 'from-teal-400 to-teal-600' :
                        content.id % 5 === 1 ? 'from-teal-400 to-teal-600' :
                        content.id % 5 === 2 ? 'from-teal-400 to-teal-600' :
                        content.id % 5 === 3 ? 'from-teal-400 to-teal-600' :
                        'from-teal-400 to-teal-600'
                      ]"
                    >
                      <Play class="text-white w-10 h-10 opacity-80" />
                    </div>
                    <div class="absolute top-3 right-3 bg-black/70 text-white px-2 py-0.5 rounded-lg text-xs font-medium">
                      {{ formatDuration(content.duration) }}
                    </div>
                  </div>

                  <div class="p-4">
                    <div class="flex items-start justify-between mb-2">
                      <div class="flex items-center gap-2 flex-1 min-w-0">
                        <img
                          :src="content.teacher?.avatar || null"
                          class="w-8 h-8 rounded-full shrink-0"
                          :alt="content.teacher?.name"
                        />
                        <div class="flex-1 min-w-0">
                          <h3 class="font-bold text-gray-900 text-sm truncate">{{ content.title }}</h3>
                          <p class="text-xs text-gray-500 truncate">{{ content.teacher?.name }}</p>
                        </div>
                      </div>
                    </div>

                    <p class="text-xs text-gray-600 mb-3 line-clamp-2">
                      {{ content.description }}
                    </p>

                    <div class="flex items-center justify-between text-xs">
                      <div class="flex items-center gap-3">
                        <span class="flex items-center text-gray-600">
                          <Eye class="mr-1 w-3 h-3" />
                          {{ formatViews(content.view_count) }}
                        </span>
                        <span class="flex items-center text-gray-600">
                          <Star class="text-yellow-400 mr-1 w-3 h-3 fill-yellow-400" />
                          {{ content.ratings_avg_score ? Number(content.ratings_avg_score).toFixed(1) : 'N/A' }}
                        </span>
                      </div>
                      <div class="flex items-center gap-1.5">
                        <span
                          v-if="content.is_free"
                          class="flex items-center px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium"
                        >
                          <LockOpen class="w-3 h-3" />
                        </span>
                        <span
                          v-if="content.subject"
                          class="px-2 py-0.5 rounded-full font-medium"
                          :style="{
                            backgroundColor: `${getSubjectColor(content.subject)}20`,
                            color: getSubjectColor(content.subject)
                          }"
                        >
                          {{ content.subject.name }}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>

              <!-- Empty State -->
              <div v-else class="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search class="w-8 h-8 text-gray-400" />
                </div>
                <h3 class="text-lg font-semibold text-gray-900 mb-2">No Content Found</h3>
                <p class="text-sm text-gray-500 mb-4">Try adjusting your filters or search query</p>
                <button
                  @click="resetLibraryFilters"
                  class="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Reset Filters
                </button>
              </div>

              <!-- Pagination -->
              <div v-if="contents.data.length > 0 && (contents.prev_page_url || contents.next_page_url)" class="flex items-center justify-center gap-3">
                <Link
                  v-if="contents.prev_page_url"
                  :href="contents.prev_page_url"
                  preserve-state
                  preserve-scroll
                  class="px-4 py-2 bg-white border border-gray-200 rounded-lg font-medium text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Previous
                </Link>
                <span class="px-3 py-2 text-gray-600 text-sm">
                  Page {{ contents.current_page }} of {{ contents.last_page }}
                </span>
                <Link
                  v-if="contents.next_page_url"
                  :href="contents.next_page_url"
                  preserve-state
                  preserve-scroll
                  class="px-4 py-2 bg-white border border-gray-200 rounded-lg font-medium text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Next
                </Link>
              </div>
            </section>
          </div>
        </div>
      </template>
    </div>
  </DashboardLayout>
</template>
